import { useEffect, useState } from 'react';

import type { TaskResponse } from '@/store/tasks/tasks.dto';
import { useTasks } from '@/store/tasks/tasks.store';

/**
 * Every task the vessel has, in local state, for a picker or an id -> title lookup.
 *
 * Deliberately not `getAllTasks`: that writes the shared `tasksList` the tasks and
 * maintenance lists render from, so a modal reaching for a task picker would replace
 * whatever cut those pages had applied with every task on the vessel — the assignee cut
 * is a server-side filter, so "My tasks" silently became everyone's. `getTasksByFilter`
 * returns the items and touches no store.
 *
 * `open` keeps the fetch out of a modal that is mounted but closed, which is how these
 * modals sit behind the surfaces that may open them.
 */
export const useTaskOptions = (open = true): TaskResponse[] => {
  const getTasksByFilter = useTasks((state) => state.getTasksByFilter);
  const [tasks, setTasks] = useState<TaskResponse[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getTasksByFilter({ limit: 1000, page: 1 })
      .then((items) => {
        if (!cancelled) setTasks(items);
      })
      .catch((error: unknown) => {
        console.error('Failed to load tasks for the picker:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [open, getTasksByFilter]);

  return tasks;
};
